/**
 * ArticleMarginalia
 * Lifts <figure> elements out of the inline article body and places them
 * into the right-hand marginal lane, anchored vertically to the paragraph
 * (or heading) that preceded them.
 *
 * Behavior:
 *  - Only active above the desktop breakpoint (≥1200px).
 *  - On mobile, figures remain inline (handled by CSS).
 *  - Re-positions on resize via ResizeObserver on the article body.
 */
export default class ArticleMarginalia {
	constructor(bodySelector = '[data-article-body]', laneSelector = '[data-article-marginalia]') {
		this.body = document.querySelector(bodySelector);
		this.lane = document.querySelector(laneSelector);
		if (!this.body || !this.lane) return;

		this.mql = window.matchMedia('(min-width: 1200px)');
		this.placeholders = [];
		this.items = [];

		this.handleChange = this.handleChange.bind(this);
		this.reposition = this.reposition.bind(this);

		if (this.mql.addEventListener) this.mql.addEventListener('change', this.handleChange);
		else this.mql.addListener(this.handleChange);

		// Always wrap all figures with lightbox anchors, regardless of breakpoint.
		this.wrapAllFiguresForLightbox();

		this.init();
		this.initCounter();
	}

	initCounter() {
		const counter = this.lane.querySelector('[data-sketch-counter]');
		if (!counter) return;
		// Total = marginal figures collected for desktop, fallback to all body figures
		const figures = this.items.length
			? this.items.map(i => i.fig)
			: Array.from(this.body.querySelectorAll('figure:not(.article-hero__figure):not(.article-chart)'));
		const total = figures.length;
		if (!total) return;

		const pad = (n) => String(n).padStart(2, '0');
		const totalEl = counter.querySelector('[data-sketch-counter-total]');
		const currentEl = counter.querySelector('[data-sketch-counter-current]');
		if (totalEl) totalEl.textContent = pad(total);
		if (currentEl) currentEl.textContent = pad(0);
		counter.hidden = false;

		// Click opens lightbox at current sketch (or first if none active yet)
		counter.addEventListener('click', () => {
			const idx = Math.max(0, this.activeIdx || 0);
			const target = figures[idx] && figures[idx].querySelector('a.glightbox');
			if (target) target.click();
		});

		// Scroll-gate: the active figure is the last one whose top has crossed
		// a fixed line at 35% of viewport height. Deterministic at section
		// boundaries (no flicker between two equally-visible figures).
		this.activeIdx = -1;
		const GATE = 0.35;
		const updateActive = () => {
			const gateY = window.innerHeight * GATE;
			let active = 0;
			for (let i = 0; i < figures.length; i++) {
				const top = figures[i].getBoundingClientRect().top;
				if (top <= gateY) active = i;
				else break;
			}
			if (active !== this.activeIdx) {
				this.activeIdx = active;
				if (currentEl) currentEl.textContent = pad(active + 1);
			}
		};
		let raf = 0;
		const onScroll = () => {
			if (raf) return;
			raf = requestAnimationFrame(() => { raf = 0; updateActive(); });
		};
		window.addEventListener('scroll', onScroll, { passive: true });
		window.addEventListener('resize', onScroll, { passive: true });
		updateActive();
	}

	wrapAllFiguresForLightbox() {
		const figures = Array.from(this.body.querySelectorAll('figure:not(.article-chart)'));
		figures.forEach((fig) => this.attachLightbox(fig));
	}

	init() {
		if (!this.mql.matches) return;
		this.collectFigures();
		this.reposition();

		// reposition on resize / font-load
		this.ro = new ResizeObserver(() => this.reposition());
		this.ro.observe(this.body);
		window.addEventListener('resize', this.reposition, { passive: true });
		if (document.fonts && document.fonts.ready) {
			document.fonts.ready.then(() => this.reposition());
		}
	}

	collectFigures() {
		const figures = Array.from(this.body.querySelectorAll(':scope > figure:not(.article-chart), :scope > p > figure:not(.article-chart)'));
		figures.forEach((fig) => {
			// anchor: previous block-level sibling (paragraph or heading)
			const anchor = fig.previousElementSibling || fig.parentElement;
			// leave a comment placeholder where the figure was for restore
			const placeholder = document.createComment('marginal-figure');
			fig.parentNode.insertBefore(placeholder, fig);

			// move into lane
			fig.classList.add('article-marginal');
			this.lane.appendChild(fig);

			this.items.push({ fig, anchor, placeholder });
		});
	}

	attachLightbox(fig) {
		// Skip the hero figure — not part of marginalia gallery
		if (fig.classList.contains('article-hero__figure')) return;
		// Diagramme sind Zeichenflaechen, keine Bilder: nicht in die Lightbox.
		if (fig.classList.contains('article-chart')) return;

		const img = fig.querySelector('img');
		if (!img) return;
		// Determine the node to wrap: if img is inside <picture>, wrap the picture
		const wrapTarget = img.parentElement && img.parentElement.tagName === 'PICTURE'
			? img.parentElement
			: img;
		// Skip if already wrapped
		if (wrapTarget.parentElement && wrapTarget.parentElement.classList.contains('glightbox')) return;

		const cap = fig.querySelector('figcaption');
		const title = cap ? cap.textContent.trim() : (img.alt || '');

		const link = document.createElement('a');
		// Lightbox needs a direct image URL — use the img's src (highest-res available)
		link.href = img.currentSrc || img.src;
		link.className = 'glightbox article-marginal__zoom';
		link.setAttribute('data-gallery', 'article-marginalia');
		if (title) {
			link.setAttribute('data-glightbox', `title: ; description: ${title.replace(/\n/g, ' ')}`);
		}
		link.setAttribute('aria-label', title || 'Bild vergrößern');

		wrapTarget.parentNode.insertBefore(link, wrapTarget);
		link.appendChild(wrapTarget);
	}

	reposition() {
		if (!this.mql.matches) return;
		const laneRect = this.lane.getBoundingClientRect();
		this.items.forEach(({ fig, anchor }) => {
			if (!anchor) return;
			const anchorRect = anchor.getBoundingClientRect();
			// position relative to lane: top of anchor in lane coordinates
			const top = anchorRect.top - laneRect.top;
			fig.style.top = `${top}rem`.replace('rem', 'px'); // px math, but the CSS uses rem-units derived from base 1px
			// We actually want px here — these are computed pixel offsets:
			fig.style.top = `${top}px`;
		});
	}

	handleChange(e) {
		if (e.matches) {
			if (!this.items.length) this.collectFigures();
			this.reposition();
		} else {
			// restore figures to inline flow on mobile
			this.items.forEach(({ fig, placeholder }) => {
				fig.classList.remove('article-marginal');
				fig.style.top = '';
				if (placeholder.parentNode) {
					placeholder.parentNode.insertBefore(fig, placeholder);
					placeholder.remove();
				}
			});
			this.items = [];
		}
	}
}
